import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Beef,
  Drumstick,
  ShoppingCart,
  Truck,
  Percent,
  IndianRupee,
} from "lucide-react";
import { api, fmtINR, fmtNum, todayISO } from "../lib/api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

const Stat = ({ label, value, sub, icon: Icon, accent = false, testid }) => (
  <div
    data-testid={testid}
    className={`border border-zinc-200 bg-white p-5 hover:border-zinc-300 transition-colors`}
  >
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">
        {label}
      </div>
      <Icon className={`h-4 w-4 ${accent ? "text-sky-600" : "text-zinc-400"}`} />
    </div>
    <div className="mt-4 font-display text-3xl font-bold tracking-tight font-mono-num">
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [stock, setStock] = useState(null);
  const [wow, setWow] = useState(null);
  const [reminders, setReminders] = useState([]);

  const load = async () => {
    const [d, t, s, w, r, u] = await Promise.all([
      api.get(`/reports/dashboard?date=${date}`),
      api.get(`/reports/trend?days=7`),
      api.get(`/reports/stock-forecast?lookback_days=7`),
      api.get(`/reports/weekly-comparison`),
      api.get(`/reminders/expenses`),
      api.get(`/party-orders/upcoming?days=2`),
    ]);
    setData(d.data);
    setTrend(t.data);
    setStock(s.data);
    setWow(w.data);
    const rem = r.data.reminders || [];
    const today = todayISO();
    (u.data || []).forEach((o) => {
      rem.push({
        category: `party-${o.id}`,
        status: o.delivery_date === today ? "overdue" : "due",
        message: `🎉 Party order #${o.order_no} · ${o.customer_name} — deliver ${o.delivery_date === today ? "TODAY" : o.delivery_date}`,
      });
    });
    setReminders(rem);
  };

  const shareSummary = () => {
    if (!data) return;
    const fmt = (n) =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(Number(n || 0));
    const lines = [
      `*EEMAN MEATS · Daily Summary*`,
      `Date: ${date}`,
      ``,
      `📊 *Sales*: ${fmt(data.total_sales)} (${data.pos_count + data.ws_count} bills)`,
      `   • POS: ${fmt(data.pos_amount)}`,
      `   • Wholesale: ${fmt(data.ws_amount)}`,
      ``,
      `📦 *Purchases*: ${fmt(data.total_purchase)}`,
      `💸 *Expenses*: ${fmt(data.total_expenses || 0)}`,
      ``,
      `${data.profit >= 0 ? "✅" : "⚠️"} *Profit*: ${fmt(data.profit)}`,
      `🔻 Wastage: ${data.wastage_pct}%`,
    ].join("\n");
    const text = encodeURIComponent(lines);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [date]);

  if (!data) {
    return (
      <div className="p-8 text-zinc-500" data-testid="dashboard-loading">
        Loading…
      </div>
    );
  }

  const catData = Object.entries(data.category_breakdown || {}).map(([k, v]) => ({
    name: k.toUpperCase(),
    value: v,
  }));

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Owner Control Room
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
            Daily Snapshot
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={shareSummary}
            data-testid="share-summary-btn"
            className="border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-1"
          >
            📲 Share on WhatsApp
          </button>
          <label className="text-xs uppercase tracking-wider text-zinc-500">
            Date
          </label>
          <input
            type="date"
            value={date}
            data-testid="dashboard-date-input"
            onChange={(e) => setDate(e.target.value)}
            className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
        </div>
      </div>

      {/* Reminders strip */}
      {reminders.filter((r) => r.status !== "missing").length > 0 && (
        <div className="mb-4 border border-amber-300 bg-amber-50 p-3 flex flex-wrap gap-3" data-testid="reminders-strip">
          <span className="text-[10px] uppercase tracking-[0.2em] text-amber-900 font-bold flex items-center gap-1">
            ⏰ Reminders
          </span>
          {reminders
            .filter((r) => r.status !== "missing")
            .map((r) => (
              <span
                key={r.category}
                data-testid={`reminder-${r.category}`}
                className={`text-xs px-2 py-1 ${
                  r.status === "overdue"
                    ? "bg-rose-600 text-white"
                    : "bg-amber-200 text-amber-900"
                }`}
              >
                {r.message}
              </span>
            ))}
        </div>
      )}

      {/* Week-over-Week */}
      {wow && (
        <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="wow-strip">
          {[
            { key: "sales", label: "Sales this week", thisV: wow.this_week.sales, lastV: wow.last_week.sales, pct: wow.delta_pct.sales },
            { key: "profit", label: "Profit this week", thisV: wow.this_week.profit, lastV: wow.last_week.profit, pct: wow.delta_pct.profit },
            { key: "bills", label: "Bills this week", thisV: wow.this_week.bills, lastV: wow.last_week.bills, pct: wow.delta_pct.bills, isCount: true },
          ].map((x) => {
            const positive = x.pct !== null && x.pct >= 0;
            return (
              <div key={x.key} className="border border-zinc-200 bg-white p-4" data-testid={`wow-${x.key}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{x.label}</div>
                  {x.pct !== null && (
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {positive ? "▲" : "▼"} {fmtNum(Math.abs(x.pct), 1)}%
                    </span>
                  )}
                </div>
                <div className="font-display text-2xl font-bold font-mono-num mt-2">
                  {x.isCount ? x.thisV : fmtINR(x.thisV)}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  vs {x.isCount ? x.lastV : fmtINR(x.lastV)} last week
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Top KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 stagger">
        <Stat
          label="Total Sales"
          value={fmtINR(data.total_sales)}
          sub={`${data.pos_count + data.ws_count + (data.party_count || 0)} orders${
            data.party_amount ? ` · party ${fmtINR(data.party_amount)}` : ""
          }`}
          icon={IndianRupee}
          testid="kpi-total-sales"
        />
        <Stat
          label="Total Purchase"
          value={fmtINR(data.total_purchase)}
          sub={`${fmtNum(data.chicken_in_kg + data.mutton_in_kg)} kg in`}
          icon={ShoppingCart}
          testid="kpi-total-purchase"
        />
        <Stat
          label="Today's Expenses"
          value={fmtINR(data.total_expenses || 0)}
          sub="all categories"
          icon={ArrowDownRight}
          testid="kpi-expenses"
        />
        <Stat
          label="Profit"
          value={fmtINR(data.profit)}
          sub="net after expenses"
          icon={data.profit >= 0 ? TrendingUp : TrendingDown}
          accent={data.profit >= 0}
          testid="kpi-profit"
        />
        <Stat
          label="Wastage %"
          value={`${fmtNum(data.wastage_pct, 1)}%`}
          sub={`${fmtNum(data.wastage_kg)} kg unaccounted`}
          icon={Percent}
          testid="kpi-wastage"
        />
      </div>

      {/* Wastage by Category */}
      {data.wastage_by_category && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3" data-testid="wastage-by-category">
          {[
            { key: "chicken", label: "Chicken Wastage", icon: Drumstick },
            { key: "mutton", label: "Mutton Wastage", icon: Beef },
            { key: "offals", label: "Offals Wastage", icon: Beef },
          ].map(({ key, label, icon: Icon }) => {
            const w = data.wastage_by_category[key] || { in_kg: 0, out_kg: 0, wastage_kg: 0, wastage_pct: 0 };
            const empty = w.in_kg === 0;
            return (
              <div
                key={key}
                className="border border-zinc-200 bg-white p-5"
                data-testid={`wastage-${key}`}
              >
                <div className="flex items-start justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">
                    {label}
                  </div>
                  <Icon className={`h-4 w-4 ${empty ? "text-zinc-300" : w.wastage_pct > 15 ? "text-rose-600" : "text-zinc-400"}`} />
                </div>
                <div
                  className={`mt-4 font-display text-3xl font-bold tracking-tight font-mono-num ${
                    empty ? "text-zinc-300" : w.wastage_pct > 15 ? "text-rose-600" : ""
                  }`}
                >
                  {empty ? "—" : `${fmtNum(w.wastage_pct, 1)}%`}
                </div>
                <div className="mt-1 text-xs text-zinc-500 font-mono-num">
                  In {fmtNum(w.in_kg, 2)} kg · Out {fmtNum(w.out_kg, 2)} kg
                </div>
                {!empty && (
                  <div className="mt-1 text-xs text-zinc-500">
                    {fmtNum(w.wastage_kg, 2)} kg unaccounted
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Channel split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
        <div className="border border-zinc-200 bg-white p-5" data-testid="card-pos">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Retail POS
            </div>
            <div className="px-2 py-0.5 bg-sky-600 text-white text-[10px] uppercase tracking-wider">
              Counter
            </div>
          </div>
          <div className="mt-3 font-display text-3xl font-bold font-mono-num">
            {fmtINR(data.pos_amount)}
          </div>
          <div className="mt-2 text-xs text-zinc-600">
            {data.pos_count} bills · {fmtNum(data.pos_kg)} kg sold
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="card-ws">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Wholesale
            </div>
            <div className="px-2 py-0.5 bg-zinc-900 text-white text-[10px] uppercase tracking-wider">
              B2B
            </div>
          </div>
          <div className="mt-3 font-display text-3xl font-bold font-mono-num">
            {fmtINR(data.ws_amount)}
          </div>
          <div className="mt-2 text-xs text-zinc-600">
            {data.ws_count} orders · {fmtNum(data.ws_kg)} kg sold
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="card-stock">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Raw Stock In
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Drumstick className="h-4 w-4 text-amber-600" />
                Chicken
              </div>
              <div className="font-mono-num text-sm font-semibold">
                {fmtNum(data.chicken_in_kg)} kg
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Beef className="h-4 w-4 text-sky-700" />
                Mutton
              </div>
              <div className="font-mono-num text-sm font-semibold">
                {fmtNum(data.mutton_in_kg)} kg
              </div>
            </div>
            <div className="border-t border-zinc-200 pt-2 flex justify-between">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">
                Cost
              </div>
              <div className="font-mono-num text-sm font-bold">
                {fmtINR(data.chicken_in_cost + data.mutton_in_cost)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
        <div className="lg:col-span-2 border border-zinc-200 bg-white p-5" data-testid="chart-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                7-Day Trend
              </div>
              <div className="font-display text-xl font-semibold">
                Sales vs Purchases
              </div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="cPos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0284c7" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0284c7" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="cWs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#18181b" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#18181b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f4f4f5" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d?.slice(5)}
                  tick={{ fontSize: 11, fill: "#71717a" }}
                  axisLine={{ stroke: "#e4e4e7" }}
                />
                <YAxis tick={{ fontSize: 11, fill: "#71717a" }} axisLine={{ stroke: "#e4e4e7" }} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #e4e4e7",
                    borderRadius: 0,
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="pos"
                  stroke="#0284c7"
                  fill="url(#cPos)"
                  strokeWidth={2}
                  name="POS"
                />
                <Area
                  type="monotone"
                  dataKey="wholesale"
                  stroke="#18181b"
                  fill="url(#cWs)"
                  strokeWidth={2}
                  name="Wholesale"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-zinc-200 bg-white p-5" data-testid="chart-category">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Today by Category
          </div>
          <div className="font-display text-xl font-semibold mb-4">Breakdown</div>
          {catData.length === 0 ? (
            <div className="text-sm text-zinc-500 py-12 text-center">
              No sales recorded today
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={catData} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#71717a" }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#09090b" }}
                    width={70}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #e4e4e7",
                      borderRadius: 0,
                      fontSize: 12,
                    }}
                    formatter={(v) => fmtINR(v)}
                  />
                  <Bar dataKey="value" fill="#0284c7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3" data-testid="quick-links">
        {[
          { to: "/pos", label: "Open POS", icon: ShoppingCart },
          { to: "/wholesale", label: "Wholesale", icon: Truck },
          { to: "/rates", label: "Set Today's Rates", icon: IndianRupee },
          { to: "/purchases", label: "Add Purchase", icon: ArrowDownRight },
        ].map(({ to, label, icon: Icon }) => (
          <a
            key={to}
            href={to}
            className="border border-zinc-200 bg-white p-4 hover:border-sky-600 hover:bg-sky-50 transition-colors flex items-center justify-between"
            data-testid={`quick-${to.slice(1) || "home"}`}
          >
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-zinc-700" />
              <span className="text-sm font-medium">{label}</span>
            </div>
            <ArrowUpRight className="h-4 w-4 text-zinc-400" />
          </a>
        ))}
      </div>

      {/* Stock forecast */}
      {stock && (
        <div className="mt-3 border border-zinc-200 bg-white p-5" data-testid="dashboard-stock">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Stock Forecast
              </div>
              <div className="font-display text-xl font-semibold">Days Until Stockout</div>
            </div>
            <a
              href="/reports"
              className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-sky-600"
            >
              Full report →
            </a>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {stock.categories.map((c) => {
              const critical = c.days_until_stockout !== null && c.days_until_stockout < 2;
              const warning =
                c.days_until_stockout !== null && c.days_until_stockout < 5 && !critical;
              return (
                <div
                  key={c.category}
                  className={`border p-4 ${
                    critical
                      ? "border-sky-600 bg-sky-50"
                      : warning
                      ? "border-amber-500 bg-amber-50"
                      : "border-zinc-200"
                  }`}
                  data-testid={`dash-stock-${c.category}`}
                >
                  <div className="flex justify-between items-baseline">
                    <div className="text-sm font-semibold uppercase tracking-wider">
                      {c.category}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      {fmtNum(c.remaining_kg)} kg left
                    </div>
                  </div>
                  <div className="font-display text-2xl font-bold font-mono-num mt-1">
                    {c.days_until_stockout === null
                      ? "—"
                      : `${c.days_until_stockout}d`}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    @{fmtNum(c.avg_daily_sold_kg)} kg/day avg
                  </div>
                  {c.freezer_kg > 0 && (
                    <div className="text-[10px] text-sky-700 mt-0.5 font-mono-num" data-testid={`dash-freezer-${c.category}`}>
                      ❄ {fmtNum(c.freezer_kg)} kg in freezer{stock.freezer_date ? ` (${stock.freezer_date})` : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
